import React, { useEffect, useState } from 'react';
import { Table, Card, CardBody, CardHeader, Input, Spinner, Button } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import cacheData from '../../cache/notifications_cache.json';

interface Job {
  id: string;
  jobTitle: string;
  seoUrl?: string;
}

interface Contractor {
  id: string;
  contractorId: string;
  businessName: string;
}

interface JobMatch {
  jobId: string;
  contractorId: string;
  matchedAt: string;
  hasViewed: boolean;
}

interface CacheData {
  matches: JobMatch[];
  jobs: { [key: string]: Job };
  contractors: { [key: string]: Contractor };
  lastUpdated: string;
}

interface NotificationRow {
  jobTitle: string;
  businessName: string;
  matchedAt: Date;
  hasViewed: boolean;
  seoJobLink: string;
  idJobLink: string;
}

const ViewJobNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isError, setIsError] = useState(false);
  const [useCachedData, setUseCachedData] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);


  const serverUrl = process.env.REACT_APP_SERVER;

  const processNotifications = (
    matches: JobMatch[],
    jobs: { [key: string]: Job },
    contractors: { [key: string]: Contractor }
  ): NotificationRow[] => {
    return matches
      .map(match => {
        const job = jobs[match.jobId];
        const contractor = contractors[match.contractorId];

        if (!job || !contractor) return null;

        return {
          jobTitle: job.jobTitle,
          businessName: contractor.businessName,
          matchedAt: new Date(match.matchedAt),
          hasViewed: match.hasViewed,
          seoJobLink: job.seoUrl
            ? `${serverUrl}/jobs/${job.seoUrl}?cid=${contractor.contractorId}`
            : `${serverUrl}/jobs/default?cid=${contractor.contractorId}`,
          idJobLink: `${serverUrl}/job-detail/${job.id}?cid=${contractor.contractorId}`
        };
      })
      .filter((row): row is NotificationRow => row !== null);
  };

  const fetchCachedData = async () => {
    try {

      const notificationRows = processNotifications(
        cacheData.matches,
        cacheData.jobs,
        cacheData.contractors
      );
      
      setNotifications(notificationRows);
    } catch (error) {
      console.error('Error fetching cached data:', error);
      setIsError(true);
    }
  };

  const fetchLiveData = async () => {
    try {
      const firestore = firebase.firestore();
      
      // Fetch all matches
      const matchesSnapshot = await firestore
        .collection('jobContractorMatches')
        .get();
      
      const matches = matchesSnapshot.docs.map(doc => ({
        ...doc.data(),
        matchedAt: doc.data().matchedAt.toDate().toISOString()
      })) as JobMatch[];

      // Get unique job IDs and contractor IDs
      const jobIds = Array.from(new Set(matches.map(m => m.jobId)));
      const contractorIds = Array.from(new Set(matches.map(m => m.contractorId)));

      // Fetch all relevant jobs
      const jobsSnapshot = await firestore
        .collection('jobs')
        .where(firebase.firestore.FieldPath.documentId(), 'in', jobIds)
        .get();
      
      const jobs: { [key: string]: Job } = {};
      jobsSnapshot.docs.forEach(doc => {
        jobs[doc.id] = {
          id: doc.id,
          ...doc.data()
        } as Job;
      });

      // Fetch all relevant contractors
      const contractorsSnapshot = await firestore
        .collection('contractors')
        .where('contractorId', 'in', contractorIds)
        .get();
      
      const contractors: { [key: string]: Contractor } = {};
      contractorsSnapshot.docs.forEach(doc => {
        contractors[doc.data().contractorId] = {
          id: doc.id,
          ...doc.data()
        } as Contractor;
      });

      const notificationRows = processNotifications(matches, jobs, contractors);
      setNotifications(notificationRows);
      setLastUpdated('Live Data');
    } catch (error) {
      console.error('Error fetching live data:', error);
      setIsError(true);
    }
  };

  useEffect(() => {
    const pageTitle = 'BidBro: Job Contractor Notifications';

    document.title = pageTitle;
  }, [ ]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setIsError(false);
      
      if (useCachedData) {
        await fetchCachedData();
      } else {
        await fetchLiveData();
      }
      
      setLoading(false);
    };

    fetchData();
  }, [useCachedData]);

  const filteredNotifications = notifications.filter(notification =>
    notification.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notification.businessName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isError) {
    return (
      <Card>
        <CardBody>
          <div className="text-center text-danger">
            Error loading notifications. Please try again later.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <h4 className="card-title mb-0">Job Notifications</h4>
          {lastUpdated && (
            <small className="text-muted">Last updated: {lastUpdated}</small>
          )}
        </div>
        <div className="d-flex gap-3">
          <Button
            color={useCachedData ? "primary" : "secondary"}
            onClick={() => setUseCachedData(true)}
            size="sm"
          >
            Use Cached Data
          </Button>
          <Button
            color={!useCachedData ? "primary" : "secondary"}
            onClick={() => setUseCachedData(false)}
            size="sm"
          >
            Use Live Data
          </Button>
          <Input
            type="text"
            placeholder="Search jobs or contractors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="table-striped table-hover">
              <thead>
                <tr>
                  <th>Job Title</th>
                  <th>Contractor Name</th>
                  <th>Matched Date</th>
                  <th>Viewed</th>
                  <th>SEO Link</th>
                  <th>Direct Link</th>
                </tr>
              </thead>
              <tbody>
                {filteredNotifications.map((notification, index) => (
                  <tr key={index}>
                    <td>{notification.jobTitle}</td>
                    <td>{notification.businessName}</td>
                    <td>{notification.matchedAt.toLocaleDateString()}</td>
                    <td>
                      <span className={`badge ${notification.hasViewed ? 'bg-success' : 'bg-warning'}`}>
                        {notification.hasViewed ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <a 
                        href={notification.seoJobLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-soft-primary"
                      >
                        SEO Link
                      </a>
                    </td>
                    <td>
                      <a 
                        href={notification.idJobLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-soft-secondary"
                      >
                        Direct Link
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {filteredNotifications.length === 0 && (
              <div className="text-center text-muted">
                No notifications found
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ViewJobNotifications;